import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Persistence-aware token storage:
//   • "Remember me" checked → localStorage (survives browser restart)
//   • "Remember me" unchecked → sessionStorage (dies on browser close)
// A tiny flag `venturaz_persist` in localStorage records the user's choice
// so subsequent tabs pick the same bucket. Cleared entirely on logout.
const TOKEN_KEY = "venturaz_token";
const PERSIST_KEY = "venturaz_persist";

// NOTE (T1 fix): declared here (before any call to scheduleSilentRefresh /
// setAuthHeader during module init) to avoid a TDZ ReferenceError
// "Cannot access '_refreshTimer' before initialization" which blanked the app.
let _refreshTimer = null;

function _storage(persistent) {
  return persistent ? localStorage : sessionStorage;
}

export function setAuthHeader(token, opts) {
  // opts?.persist === undefined  →  keep the existing choice from PERSIST_KEY
  // opts?.persist === true|false →  switch to that mode
  if (token) {
    let persistent;
    if (opts && typeof opts.persist === "boolean") {
      persistent = opts.persist;
      localStorage.setItem(PERSIST_KEY, persistent ? "1" : "0");
    } else {
      persistent = localStorage.getItem(PERSIST_KEY) === "1";
    }
    // Clear the OTHER bucket to avoid stale tokens after a mode-switch.
    if (persistent) sessionStorage.removeItem(TOKEN_KEY);
    else localStorage.removeItem(TOKEN_KEY);
    _storage(persistent).setItem(TOKEN_KEY, token);
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    scheduleSilentRefresh(token);
  } else {
    delete api.defaults.headers.common.Authorization;
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PERSIST_KEY);
    if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null; }
  }
}

function _loadToken() {
  if (typeof window === "undefined") return null;
  // sessionStorage wins if both exist (rare after mode-switch race)
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}
const stored = _loadToken();
if (stored) {
  api.defaults.headers.common.Authorization = `Bearer ${stored}`;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d?.msg === "string" ? d.msg : JSON.stringify(d))).join(" • ");
  }
  return JSON.stringify(detail);
}

// ---------------------------------------------------------------------------
// Silent JWT refresh — hits /auth/refresh ~5 min before the current 1h JWT
// expires so long-open tabs never hit a 401 mid-flow. A single global timer
// is shared across all instances of api.js in the SPA.
// ---------------------------------------------------------------------------
const REFRESH_LEEWAY_MS = 5 * 60 * 1000; // refresh 5 min before expiry

function _decodeJwt(token) {
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch { return null; }
}

async function _doSilentRefresh() {
  try {
    const { data } = await api.post("/auth/refresh");
    if (data?.token) {
      // Use existing persist mode (don't flip it)
      setAuthHeader(data.token);
    }
  } catch {
    // On failure the next real API call will 401 and the app will bounce
    // the user to /login — no need to force logout here.
  }
}

export function scheduleSilentRefresh(token) {
  if (typeof window === "undefined") return;
  if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null; }
  const payload = _decodeJwt(token);
  const expSec = payload?.exp;
  if (!expSec) return;
  const msUntilRefresh = expSec * 1000 - Date.now() - REFRESH_LEEWAY_MS;
  // Fire immediately if the token is already within the leeway window.
  const delay = Math.max(1000, msUntilRefresh);
  _refreshTimer = setTimeout(_doSilentRefresh, delay);
}

// 401 interceptor — attempt a one-shot silent refresh, then retry the request.
// Only fires when the SPA actually has a stored token; otherwise a 401 on a
// public page (e.g. /login) would trigger a noisy /auth/refresh 401.
let _refreshInFlight = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const isRefreshCall = original.url && original.url.includes("/auth/refresh");
    const hasAuthHeader = !!api.defaults.headers.common.Authorization;
    if (status !== 401 || original._retried || isRefreshCall || !hasAuthHeader) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      if (!_refreshInFlight) _refreshInFlight = _doSilentRefresh();
      await _refreshInFlight;
    } finally {
      _refreshInFlight = null;
    }
    // If the refresh worked, api.defaults.headers.common.Authorization is fresh.
    const newAuth = api.defaults.headers.common.Authorization;
    if (newAuth) {
      original.headers = original.headers || {};
      original.headers.Authorization = newAuth;
      return api(original);
    }
    return Promise.reject(error);
  },
);

// Start the silent-refresh timer at boot if we already have a token.
// (Placed at the end of module init so all `let`/`const` bindings above are
// initialised — calling it earlier caused a TDZ ReferenceError that blanked
// the whole app on any hard page load while signed in.)
if (stored) scheduleSilentRefresh(stored);

export default api;
